/**
 * VirtualDeviceService for managing virtual devices
 * This service integrates with the API to create and manage virtual devices
 */

import ApiService from './ApiService';
import { Device } from '../components/DeviceCard';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

class VirtualDeviceService {
  private authInitialized = false;
  private cachedUserId: string = '';
  
  constructor() {
    // Listen for authentication events
    this.setupAuthListener();
  }
  
  /**
   * Set up listener for authentication events
   */
  private setupAuthListener(): void {
    Hub.listen('auth', ({ payload }) => {
      const { event } = payload;
      
      if (event === 'signedIn') {
        console.log('User signed in, clearing cached user ID');
        this.cachedUserId = '';
      } else if (event === 'signedOut') {
        console.log('User signed out, clearing cached user ID');
        this.cachedUserId = '';
      } else if (event === 'tokenRefresh') {
        console.log('Auth token refreshed, clearing cached user ID');
        this.cachedUserId = '';
      }
    });
  }
  /**
   * Get the current user ID
   * @returns Promise resolving to the user ID
   */
  private async getUserId(): Promise<string> {
    // Return cached user ID if available
    if (this.cachedUserId && this.cachedUserId.length > 0) {
      console.log('Using cached user ID:', this.cachedUserId);
      return this.cachedUserId;
    }
    
    try {
      // Try multiple authentication methods in sequence
      
      // Method 1: Get the current authenticated user
      try {
        const auth = await import('aws-amplify/auth');
        const currentUser = await auth.getCurrentUser();
        console.log('Current user:', currentUser);
        
        if (currentUser && currentUser.userId) {
          console.log('Using user ID from getCurrentUser:', currentUser.userId);
          this.cachedUserId = currentUser.userId;
          return this.cachedUserId;
        }
        
        if (currentUser && currentUser.username) {
          console.log('Using username from getCurrentUser:', currentUser.username);
          this.cachedUserId = currentUser.username;
          return this.cachedUserId;
        }
      } catch (userError) {
        console.warn('Could not get current user:', userError);
      }
      
      // Method 2: Get the ID from the auth session token
      try {
        const session = await fetchAuthSession();
        console.log('Auth session:', session);
        
        if (session && session.tokens && session.tokens.idToken) {
          const payload = session.tokens.idToken.payload;
          console.log('Auth token payload:', payload);
          
          if (payload.email) {
            console.log('Using email from token payload:', payload.email);
            this.cachedUserId = payload.email as string;
            return this.cachedUserId;
          }
          
          if (payload.sub) {
            console.log('Using user ID from token payload (sub):', payload.sub);
            this.cachedUserId = payload.sub as string;
            return this.cachedUserId;
          }
          
          if (payload['cognito:username']) {
            console.log('Using cognito:username from token:', payload['cognito:username']);
            this.cachedUserId = payload['cognito:username'] as string;
            return this.cachedUserId;
          }
        }
      } catch (sessionError) {
        console.warn('Could not get auth session:', sessionError);
      }
      
      // Method 3: Check localStorage for Amplify auth data
      try {
        // Check different possible localStorage keys
        const storageKeys = [
          'amplify-signin-with-hostedUI',
          'CognitoIdentityServiceProvider.lastAuthUser',
          'amplify-authenticator-authState'
        ];
        
        for (const key of storageKeys) {
          const storedData = localStorage.getItem(key);
          if (storedData) {
            try {
              const userData = JSON.parse(storedData);
              if (userData && userData.username) {
                console.log(`Using user ID from localStorage (${key}):`, userData.username);
                this.cachedUserId = userData.username;
                return this.cachedUserId;
              }
            } catch (parseError) {
              // If it's not JSON, it might be the username directly
              if (typeof storedData === 'string' && storedData.length > 0) {
                console.log(`Using user ID from localStorage (${key}):`, storedData);
                this.cachedUserId = storedData;
                return this.cachedUserId;
              }
            }
          }
        }
      } catch (localStorageError) {
        console.warn('Error accessing localStorage:', localStorageError);
      }
      
      // Method 4: Check URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const userIdParam = urlParams.get('userId');
      if (userIdParam) {
        console.log('Using user ID from URL parameter:', userIdParam);
        this.cachedUserId = userIdParam;
        return this.cachedUserId;
      }
      
      // Method 5: Use anonymous as fallback
      console.warn('No user ID found, using anonymous as fallback');
      this.cachedUserId = 'anonymous';
      return this.cachedUserId;
    } catch (error) {
      console.error('Error in getUserId:', error);
      throw new Error('Failed to get authenticated user ID');
    }
  }
  
  /**
   * Create a new virtual device
   * @param name Device name
   * @param type Device type
   * @param room Room name
   * @param initialState Initial device state
   * @returns The created device
   */
  async createVirtualDevice(
    name: string,
    type: 'light' | 'thermostat' | 'fan' | 'switch' | 'lock' | 'camera',
    room: string,
    initialState: Record<string, any> = {}
  ): Promise<Device | null> {
    try {
      // Ensure WebSocket connection is established before creating device
      try {
        await ApiService.connectWebSocket();
      } catch (wsError) {
        console.warn('Could not connect to WebSocket, continuing with device creation:', wsError);
      }
      
      // Get the current user ID
      const userId = await this.getUserId();
      
      if (!userId) {
        throw new Error('Could not determine user ID');
      }
      
      // Generate a unique ID for the virtual device
      const deviceId = `virtual-${type}-${Date.now()}`;
      
      console.log('Creating virtual device with user ID:', userId);
      
      // Set isVirtual flag explicitly
      const deviceData = {
        id: deviceId,
        deviceId: deviceId, // Ensure both id and deviceId are set
        userId,
        name,
        deviceType: type,
        room,
        location: room, // Set location to match room
        isVirtual: true,
        state: initialState,
        capabilities: this.getDefaultCapabilities(type)
      };
      
      console.log('Registering virtual device with data:', deviceData);
      
      // Create the device in the API
      const apiDevice = await ApiService.registerDevice(deviceData);
      
      if (!apiDevice) {
        console.error('API returned null device');
        throw new Error('Failed to create virtual device - API returned null');
      }
      
      console.log('Created device:', apiDevice);
      
      // Subscribe to updates for this device
      try {
        await ApiService.subscribeToDevice(deviceId);
      } catch (subError) {
        console.warn('Could not subscribe to device updates:', subError);
      }
      
      // Map the API device to our frontend device format
      return {
        id: deviceId,
        name: name,
        type: type,
        room: room,
        isVirtual: true,
        state: initialState
      };
    } catch (error) {
      console.error('Error creating virtual device:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to create device: ${errorMessage}`);
    }
  }
  
  /**
   * Get default capabilities for a device type
   * @param deviceType The device type
   * @returns Array of capability strings
   */
  private getDefaultCapabilities(deviceType: string): string[] {
    const capabilities: Record<string, string[]> = {
      'light': ['power', 'brightness', 'color'],
      'thermostat': ['power', 'temperature', 'mode'],
      'fan': ['power', 'speed'],
      'switch': ['power'],
      'lock': ['lock', 'unlock'],
      'camera': ['power', 'record', 'snapshot']
    };
    
    return capabilities[deviceType] || ['power'];
  }
  
  /**
   * Create a set of demo virtual devices
   * @returns Array of created devices
   */
  async createDemoVirtualDevices(): Promise<Device[]> {
    try {
      const devices: Device[] = [];
      
      // Create a virtual light
      const light = await this.createVirtualDevice(
        'Virtual Living Room Light',
        'light',
        'Living Room',
        { power: 'off', brightness: 80 }
      );
      
      if (light) devices.push(light);
      
      // Create a virtual thermostat
      const thermostat = await this.createVirtualDevice(
        'Virtual Bedroom Thermostat',
        'thermostat',
        'Bedroom',
        { power: 'on', temperature: 72, targetTemperature: 70, mode: 'cool' }
      );
      
      if (thermostat) devices.push(thermostat);
      
      // Create a virtual fan
      const fan = await this.createVirtualDevice(
        'Virtual Kitchen Fan',
        'fan',
        'Kitchen',
        { power: 'off', speed: 2 }
      );
      
      if (fan) devices.push(fan);
      
      return devices;
    } catch (error) {
      console.error('Error creating demo virtual devices:', error);
      return [];
    }
  }
  
  /**
   * Check if a device is virtual based on its ID
   * @param deviceId Device ID
   * @returns True if the device is virtual, false otherwise
   */
  isVirtualDevice(deviceId: string): boolean {
    return deviceId.startsWith('virtual-');
  }
}

export default new VirtualDeviceService();